// Composant générique de saisie de facture (client OU fournisseur).
// Réutilise SelecteurTiers (type imposé) et SelecteurCompte.
// Aucun terme comptable technique n'est affiché à l'utilisateur.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { API, formatErreur } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import SelecteurTiers from "@/components/SelecteurTiers";
import SelecteurCompte from "@/components/SelecteurCompte";
import DatePicker from "@/components/DatePicker";
import JournalEcritures from "@/components/JournalEcritures";
import ApercuEcritures from "@/components/ApercuEcritures";
import { Plus, Trash2, Loader2, FileText, Printer, Copy } from "lucide-react";
import { EnvoiFactureBouton } from "@/components/EnvoiFactureBouton";

const fmt = (n) => `${Math.round(n || 0).toLocaleString("fr-FR")} XPF`;
const aujourdHui = () => new Date().toISOString().slice(0, 10);
const ligneVide = () => ({
  code_article: "", description: "", compte: null,
  quantite: "1", prix_unitaire_ht: "", taux_tva: 13,
});

export default function SaisieFacture({ config }) {
  const {
    typeFacture, endpoint, titre, sousTitre, icone: Icone,
    tiersLabel, selecteurTiersTestId, pageTestId, descriptionPlaceholder,
    showReference, referenceLabel, journalCode, libelleJournal, compteTva,
  } = config;

  const [tiers, setTiers] = useState(null);
  const [dateFacture, setDateFacture] = useState(aujourdHui());
  const [dateEcheance, setDateEcheance] = useState("");
  const [referenceExterne, setReferenceExterne] = useState("");
  const [moyenPaiement, setMoyenPaiement] = useState("");
  const [notes, setNotes] = useState("");
  const [lignes, setLignes] = useState([ligneVide()]);
  const [taux, setTaux] = useState([]);
  const [numero, setNumero] = useState("");
  const [enregistrement, setEnregistrement] = useState(false);
  const [factures, setFactures] = useState([]);
  const [resetSeq, setResetSeq] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const formRef = useRef(null);

  const chargerFactures = useCallback(async () => {
    try {
      const { data } = await api.get("/factures", { params: { type: typeFacture } });
      setFactures(data || []);
    } catch {
      setFactures([]);
    }
  }, [typeFacture]);

  const chargerNumero = useCallback(async () => {
    try {
      const { data } = await api.get("/factures/prochain-numero", { params: { type: typeFacture } });
      setNumero(data.numero);
    } catch { /* ignore */ }
  }, [typeFacture]);

  useEffect(() => {
    api.get("/taux-tva").then(({ data }) => setTaux(data || [])).catch(() => setTaux([]));
    chargerNumero();
    chargerFactures();
  }, [chargerFactures, chargerNumero]);

  const majLigne = (i, champ, valeur) =>
    setLignes((ls) => ls.map((l, idx) => (idx === i ? { ...l, [champ]: valeur } : l)));
  const ajouterLigne = () => setLignes((ls) => [...ls, ligneVide()]);
  const supprimerLigne = (i) => setLignes((ls) => (ls.length > 1 ? ls.filter((_, idx) => idx !== i) : ls));

  const calcLigne = (l) => {
    const qte = parseFloat(l.quantite) || 0;
    const pu = parseFloat(l.prix_unitaire_ht) || 0;
    const ht = Math.round(qte * pu);
    const tva = Math.round((ht * (l.taux_tva || 0)) / 100);
    return { ht, tva, ttc: ht + tva };
  };
  const totaux = lignes.reduce(
    (acc, l) => { const c = calcLigne(l); return { ht: acc.ht + c.ht, tva: acc.tva + c.tva, ttc: acc.ttc + c.ttc }; },
    { ht: 0, tva: 0, ttc: 0 }
  );

  // Aperçu des écritures générées (VT : 411 débit TTC / 7xx crédit HT / 445710 TVA
  // collectée ; AC : 6xx débit HT / 445660 TVA déductible / 401 crédit TTC).
  const apercuLignes = (() => {
    const vente = typeFacture === "client";
    const compteTiers = tiers?.compte || (vente ? "411…" : "401…");
    const nomTiers = tiers?.nom || (vente ? "Client" : "Fournisseur");
    const lignesPC = lignes
      .filter((l) => l.compte && calcLigne(l).ht > 0)
      .map((l) => {
        const ht = calcLigne(l).ht;
        return vente
          ? { compte: l.compte.numero, libelle: l.compte.libelle, debit: 0, credit: ht }
          : { compte: l.compte.numero, libelle: l.compte.libelle, debit: ht, credit: 0 };
      });
    const ligneTva = totaux.tva > 0
      ? (vente
          ? { compte: compteTva || "445710", libelle: "TVA collectée", debit: 0, credit: totaux.tva }
          : { compte: compteTva || "445660", libelle: "TVA déductible", debit: totaux.tva, credit: 0 })
      : null;
    if (vente) {
      return [{ compte: compteTiers, libelle: nomTiers, debit: totaux.ttc, credit: 0 },
        ...lignesPC, ...(ligneTva ? [ligneTva] : [])];
    }
    return [...lignesPC, ...(ligneTva ? [ligneTva] : []),
      { compte: compteTiers, libelle: nomTiers, debit: 0, credit: totaux.ttc }];
  })();

  const reinitialiser = () => {
    setTiers(null);
    setDateFacture(aujourdHui());
    setDateEcheance("");
    setReferenceExterne("");
    setMoyenPaiement("");
    setNotes("");
    setLignes([ligneVide()]);
    setResetSeq((s) => s + 1);
  };

  // Duplication d'une facture existante (saisies récurrentes : abonnements, loyers…).
  const dupliquer = (f) => {
    setTiers({ id: f.tiers_id, nom: f.tiers_nom, compte: f.tiers_compte });
    setDateFacture(aujourdHui());
    setDateEcheance("");
    setReferenceExterne(f.reference_externe || "");
    setMoyenPaiement(f.moyen_paiement || "");
    setNotes(f.notes || "");
    setLignes((f.lignes || []).map((l) => {
      const qte = l.quantite ?? 1;
      const pu = l.prix_unitaire_ht != null
        ? Math.round(l.prix_unitaire_ht)
        : Math.round((l.montant_ht || 0) / (qte || 1));
      return {
        code_article: l.code_article || "",
        description: l.description || "",
        compte: { numero: l.compte, libelle: l.libelle_compte || "" },
        quantite: String(qte),
        prix_unitaire_ht: pu ? String(pu) : "",
        taux_tva: l.taux_tva,
      };
    }));
    setResetSeq((s) => s + 1);
    toast.success(`Facture ${f.numero} dupliquée. Vérifiez et enregistrez.`);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };

  const enregistrer = async () => {
    if (!tiers) {
      toast.error(`Veuillez sélectionner un ${tiersLabel.toLowerCase()}.`);
      return;
    }
    const lignesValides = lignes.filter((l) => l.description.trim() && l.compte && calcLigne(l).ht > 0);
    if (lignesValides.length === 0) {
      toast.error("Ajoutez au moins une ligne complète (description, catégorie, quantité et prix).");
      return;
    }
    setEnregistrement(true);
    try {
      await api.post(endpoint, {
        tiers_id: tiers.id,
        date_facture: dateFacture,
        date_echeance: dateEcheance || null,
        reference_externe: referenceExterne || null,
        moyen_paiement: moyenPaiement || null,
        notes: notes || null,
        lignes: lignesValides.map((l) => {
          const qte = parseFloat(l.quantite) || 1;
          const pu = Math.round(parseFloat(l.prix_unitaire_ht) || 0);
          return {
            description: l.description, compte: l.compte.numero,
            libelle_compte: l.compte.libelle,
            code_article: l.code_article || null,
            quantite: qte, prix_unitaire_ht: pu,
            montant_ht: Math.round(qte * pu), taux_tva: l.taux_tva,
          };
        }),
      });
      toast.success("Facture enregistrée avec succès.");
      reinitialiser();
      chargerNumero();
      chargerFactures();
      setRefreshKey((k) => k + 1);
    } catch (e) {
      toast.error(formatErreur(e.response?.data?.detail));
    } finally {
      setEnregistrement(false);
    }
  };

  return (
    <div className="fenua-fade-up" data-testid={pageTestId}>
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-lg bg-[#E8F4F4] flex items-center justify-center">
          <Icone className="h-6 w-6 text-[#1D3D3D]" />
        </div>
        <div>
          <h1 data-testid="titre-section" className="text-3xl font-black tracking-tight text-[#1D3D3D]">{titre}</h1>
          <p className="text-sm text-[#1D3D3D]/70">{sousTitre}</p>
        </div>
      </div>

      {journalCode && (
        <div className="mt-6">
          <JournalEcritures code={journalCode} libelleJournal={libelleJournal} refreshKey={refreshKey} />
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 xl:grid-cols-3 gap-6" ref={formRef}>
        <div className="xl:col-span-2 bg-white border border-[#4A8A8A]/20 rounded-lg shadow-sm p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-1.5">
              <Label className="text-[#1D3D3D] font-semibold text-sm">{tiersLabel}</Label>
              <SelecteurTiers key={resetSeq} typeFixe={typeFacture} onSelect={setTiers} testId={selecteurTiersTestId} valeurInitiale={tiers} />
              {showReference && (
                <div className="pt-2 space-y-1.5">
                  <Label className="text-[#1D3D3D] font-semibold text-sm">{referenceLabel || "Référence"}</Label>
                  <Input data-testid="facture-reference-externe" value={referenceExterne}
                    onChange={(e) => setReferenceExterne(e.target.value)}
                    placeholder="N° de la facture du fournisseur"
                    className="bg-white border-[#4A8A8A]/30 focus-visible:ring-[#4A8A8A]" />
                </div>
              )}
            </div>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-[#1D3D3D] font-semibold text-sm">Numéro</Label>
                <Input data-testid="facture-numero" value={numero} readOnly className="bg-[#E8F4F4] border-[#4A8A8A]/30 font-mono" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-[#1D3D3D] font-semibold text-sm">Date</Label>
                  <DatePicker testId="facture-date" value={dateFacture} onChange={setDateFacture} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[#1D3D3D] font-semibold text-sm">Échéance</Label>
                  <DatePicker testId="facture-echeance" value={dateEcheance} onChange={setDateEcheance} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#1D3D3D] font-semibold text-sm">Mode de règlement</Label>
                <Select value={moyenPaiement} onValueChange={setMoyenPaiement}>
                  <SelectTrigger data-testid="facture-moyen-paiement" className="bg-white border-[#4A8A8A]/30 h-9">
                    <SelectValue placeholder="Sélectionner…" />
                  </SelectTrigger>
                  <SelectContent>
                    {["CB", "Chèque", "Virement", "Espèces", "Prélèvement"].map((m) => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <Label className="text-[#1D3D3D] font-semibold text-sm">Détail de la facture</Label>
            {lignes.map((l, i) => {
              const c = calcLigne(l);
              return (
                <div key={`${resetSeq}-${i}`} data-testid={`facture-ligne-${i}`} className="rounded-lg border border-[#4A8A8A]/20 p-3 space-y-3 bg-[#E8F4F4]/30">
                  <div className="flex gap-2">
                    <Input data-testid={`ligne-code-${i}`} value={l.code_article}
                      onChange={(e) => majLigne(i, "code_article", e.target.value)}
                      placeholder="Code (facultatif)"
                      className="w-32 shrink-0 bg-white border-[#4A8A8A]/30 focus-visible:ring-[#4A8A8A]" />
                    <Input data-testid={`ligne-description-${i}`} value={l.description}
                      onChange={(e) => majLigne(i, "description", e.target.value)}
                      placeholder={descriptionPlaceholder}
                      className="bg-white border-[#4A8A8A]/30 focus-visible:ring-[#4A8A8A]" />
                    <button type="button" data-testid={`supprimer-ligne-${i}`} onClick={() => supprimerLigne(i)}
                      className="shrink-0 h-10 w-10 rounded-md flex items-center justify-center text-red-500 hover:bg-red-50 disabled:opacity-30"
                      disabled={lignes.length === 1}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-12 gap-2 items-center">
                    <div className="col-span-2 sm:col-span-4">
                      <SelecteurCompte testId={`ligne-compte-${i}`} valeurInitiale={l.compte}
                        onSelect={(compte) => majLigne(i, "compte", compte)} placeholder="Catégorie" />
                    </div>
                    <div className="sm:col-span-1">
                      <Input type="number" step="1" min="0" inputMode="numeric" data-testid={`ligne-quantite-${i}`}
                        value={l.quantite} onChange={(e) => majLigne(i, "quantite", e.target.value)}
                        placeholder="Qté" title="Quantité"
                        className="bg-white border-[#4A8A8A]/30 focus-visible:ring-[#4A8A8A]" />
                    </div>
                    <div className="sm:col-span-3">
                      <Input type="number" step="1" min="0" inputMode="numeric" data-testid={`ligne-pu-${i}`}
                        value={l.prix_unitaire_ht} onChange={(e) => majLigne(i, "prix_unitaire_ht", e.target.value)}
                        placeholder="P.U. HT"
                        className="bg-white border-[#4A8A8A]/30 focus-visible:ring-[#4A8A8A]" />
                    </div>
                    <div className="sm:col-span-2">
                      <Select value={String(l.taux_tva)} onValueChange={(v) => majLigne(i, "taux_tva", parseInt(v, 10))}>
                        <SelectTrigger data-testid={`ligne-tva-${i}`} className="bg-white border-[#4A8A8A]/30">
                          <SelectValue placeholder="TVA" />
                        </SelectTrigger>
                        <SelectContent>
                          {taux.map((t) => (<SelectItem key={t.taux} value={String(t.taux)}>{t.taux}%</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="sm:col-span-2 text-right text-sm font-semibold text-[#1D3D3D]" data-testid={`ligne-ttc-${i}`}>{fmt(c.ttc)}</div>
                  </div>
                </div>
              );
            })}
            <Button type="button" variant="secondary" data-testid="ajouter-ligne" onClick={ajouterLigne}
              className="bg-[#E8F4F4] text-[#1D3D3D] hover:bg-[#4A8A8A]/20 border border-[#4A8A8A]/20">
              <Plus className="h-4 w-4 mr-1" /> Ajouter une ligne
            </Button>
          </div>

          <div className="space-y-1.5">
            <Label className="text-[#1D3D3D] font-semibold text-sm">Notes (facultatif)</Label>
            <Input data-testid="facture-notes" value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Mentions complémentaires…"
              className="bg-white border-[#4A8A8A]/30 focus-visible:ring-[#4A8A8A]" />
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white border border-[#4A8A8A]/20 rounded-lg shadow-sm p-6">
            <h3 className="font-bold text-[#1D3D3D] mb-4">Récapitulatif</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-[#1D3D3D]/70">Total HT</span><span data-testid="total-ht" className="font-medium text-[#1D3D3D]">{fmt(totaux.ht)}</span></div>
              <div className="flex justify-between"><span className="text-[#1D3D3D]/70">TVA</span><span data-testid="total-tva" className="font-medium text-[#1D3D3D]">{fmt(totaux.tva)}</span></div>
              <div className="h-px bg-[#4A8A8A]/20 my-2" />
              <div className="flex justify-between text-base"><span className="font-bold text-[#1D3D3D]">Total TTC</span><span data-testid="total-ttc" className="font-black text-[#1D3D3D]">{fmt(totaux.ttc)}</span></div>
            </div>
            <Button data-testid="bouton-enregistrer-facture" onClick={enregistrer} disabled={enregistrement}
              className="w-full mt-6 bg-[#1D3D3D] hover:bg-[#2D5A5A] text-white font-semibold h-11">
              {enregistrement ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer la facture"}
            </Button>
          </div>

          <ApercuEcritures lignes={apercuLignes} />

          <div className="bg-white border border-[#4A8A8A]/20 rounded-lg shadow-sm p-6">
            <h3 className="font-bold text-[#1D3D3D] mb-4 flex items-center gap-2">
              <FileText className="h-4 w-4 text-[#4A8A8A]" /> Factures récentes
            </h3>
            {factures.length === 0 ? (
              <p className="text-sm text-[#1D3D3D]/60">Aucune facture pour le moment.</p>
            ) : (
              <div className="space-y-2" data-testid="liste-factures">
                {factures.slice(0, 8).map((f) => (
                  <div key={f.id} data-testid={`facture-item-${f.id}`} className="flex items-center justify-between gap-2 text-sm py-2 border-b border-[#4A8A8A]/10 last:border-0">
                    <div className="min-w-0">
                      <p className="font-mono text-[#1D3D3D] font-medium truncate">{f.numero}</p>
                      <p className="text-[#1D3D3D]/60 text-xs truncate">{f.tiers_nom}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="font-semibold text-[#1D3D3D]">{fmt(f.total_ttc)}</span>
                      <button type="button" data-testid={`dupliquer-facture-${f.id}`}
                        onClick={() => dupliquer(f)}
                        title="Dupliquer cette facture"
                        className="h-8 w-8 rounded-md flex items-center justify-center text-[#1D3D3D] hover:bg-[#E8F4F4] transition-colors">
                        <Copy className="h-4 w-4" />
                      </button>
                      {typeFacture === "client" && (
                        <button type="button" data-testid={`imprimer-facture-${f.id}`}
                          onClick={() => window.open(`${API}/factures/${f.id}/pdf`, "_blank")}
                          title="Imprimer la facture"
                          className="h-8 w-8 rounded-md flex items-center justify-center text-[#1D3D3D] hover:bg-[#E8F4F4] transition-colors">
                          <Printer className="h-4 w-4" />
                        </button>
                      )}
                      {typeFacture === "client" && (
                        <EnvoiFactureBouton factureId={f.id} numero={f.numero}
                          testId={`envoyer-facture-${f.id}`} />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
